import { clsx } from 'clsx';
import Link from 'next/link';
import { type Product } from '@/services/products/types';
import { Scene7Image } from '@/components/Scene7Image';
import { DEFAULT_IMAGE_PLP } from '@/components/constants/product';
import styles from '../ProductPod.module.scss';
import BREAKPOINTS from '@/common/constants/breakpoints';
import { useId } from 'react';

const maxWidthForLargeOrExtraLargeViewport = '185px';
const maxWidthForMediumOr1024pxViewport = '275px';
const maxWidthForSmallViewport = '326px';
const maxWidthFor375pxViewport = '127px';
const maxWidthForExtraSmallViewport = '85px';

export function ProductPodImage({
  isProductInTrolley,
  onRedirect,
  product,
}: {
  product: Product;
  isProductInTrolley?: boolean;
  onRedirect: () => void;
}) {
  const { availability, formattedPrice, image, name, slug, sponsored } = product;
  const sizes = `(min-width: ${BREAKPOINTS.XL}px) ${maxWidthForLargeOrExtraLargeViewport}, 
(min-width: 1024px) ${maxWidthForMediumOr1024pxViewport},
(min-width: ${BREAKPOINTS.LG}px) ${maxWidthForLargeOrExtraLargeViewport},
(min-width: ${BREAKPOINTS.MD}px) ${maxWidthForMediumOr1024pxViewport},
(min-width: ${BREAKPOINTS.SM}px) ${maxWidthForSmallViewport},
(min-width: 375px) ${maxWidthFor375pxViewport},
${maxWidthForExtraSmallViewport}
`;

  const imageId = useId();
  const badgeId = useId();
  const showSponsoredBadge = sponsored && !isProductInTrolley;
  const ids = showSponsoredBadge ? [badgeId, imageId] : [imageId];

  return (
    <>
      <Link
        aria-labelledby={ids.join(' aria-delimiter ')}
        className={styles.imageLink}
        href={{ pathname: '/products/[slug]', query: { slug } }}
        onClick={onRedirect}
      >
        <Scene7Image
          alt={image?.alt ?? name}
          aria-hidden={true}
          className={clsx({
            [styles.lightenImage]: !availability.isOnStock || !formattedPrice,
            [styles.productImage]: true,
          })}
          crossOrigin="anonymous"
          fallbackImage={DEFAULT_IMAGE_PLP}
          id={imageId}
          sizes={sizes}
          src={image?.src}
        />
      </Link>
      {showSponsoredBadge && (
        <span className={styles.sponsoredBadge} id={badgeId}>
          Sponsored
        </span>
      )}
    </>
  );
}
